/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import useForm from "react-hook-form";
import {
  authCheckCallApi,
  parseGraphQlError,
  urlValidator,
} from "../../Authenticator/utils";
import {
  Form,
  Button,
  Dimmer,
  Loader,
  Modal,
  Transition,
  Grid,
  Dropdown,
  DropdownProps,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import { ValidationError } from "../../Authenticator";
import { useMutation } from "@apollo/react-hooks";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { planOptions } from "./OrganizationsList";
import { createOrganization } from "./queries";
import { countryOptions } from "../../Authenticator/countries";

interface CreateOrgDialogProps {
  onClose: any;
}

const CreateOrgDialog: React.FC<CreateOrgDialogProps> = ({ onClose }) => {
  type FormData = {
    companyName: string;
    domain: string;
    plan: string;
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressCountry: string;
    addressState: string;
    addressZip: string;
    addressTelephone1: string;
    addressTelephone2: string;
  };

  const emptyInputs = {
    companyName: "",
    domain: "",
    plan: "free",
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressCountry: "",
    addressState: "",
    addressZip: "",
    addressTelephone1: "",
    addressTelephone2: "",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const { register, handleSubmit, errors, setValue } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  useEffect(() => {
    register({ name: "addressCountry" }, { required: true });
  }, [register]);

  const [createOrganizationMutation] = useMutation(createOrganization, {
    onError: (e) => {
      setLoading(false);
      if (!message) {
        setMessage({
          title: "Create Organization",
          message: `Unable to create organization. ${parseGraphQlError(e)}`,
        });
      }
    },
    onCompleted: () => {
      setLoading(false);
      setCreateSuccess(true);
      setMessage({
        title: "Create Organization",
        message: "The organization was created successfully.",
      });
    },
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleDropdownChange = (_: any, data: DropdownProps) => {
    setValue(data.name, data.value);
    setInputs((inputs) => ({ ...inputs, [data.name]: data.value }));
  };

  const onCreateOrg = () => {
    setLoading(true);
    authCheckCallApi(createOrganizationMutation, {
      variables: {
        organization: {
          name: inputs.companyName,
          domain: inputs.domain,
          plan: inputs.plan,
          address: {
            line1: inputs.addressLine1,
            line2: inputs.addressLine2,
            city: inputs.addressCity,
            country: inputs.addressCountry,
            zip: inputs.addressZip,
            state: inputs.addressState,
            telephone1: inputs.addressTelephone1,
            telephone2: inputs.addressTelephone2,
          },
        },
      },
    });
  };

  return (
    <Transition animation="fade in" duration="250">
      <Modal
        size="small"
        open
        onClose={() => onClose()}
        closeOnEscape={true}
        closeOnDimmerClick={false}
      >
        {loading && (
          <Dimmer active={true}>
            <Loader size="large" active={true} />
          </Dimmer>
        )}

        <Form onSubmit={handleSubmit(onCreateOrg)}>
          <input type="submit" hidden />

          <div
            className={cStyles.closeButton}
            style={{ margin: "0 1rem 0 0" }}
            onClick={() => onClose()}
          >
            <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
          </div>

          <div style={{ margin: "2rem 0 1rem 2rem" }}>
            <span className={cStyles.title}>Create a New Organization</span>
          </div>

          <div
            style={{
              maxHeight: "75vh",
              overflow: "auto",
              paddingRight: "5px",
            }}
          >
            <Grid stackable padded style={{ padding: "15px" }}>
              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Company Name</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required error={!!errors.companyName}>
                    <input
                      autoFocus
                      name="companyName"
                      key="companyName"
                      className={styles.myAccountInput}
                      placeholder="Enter company name"
                      autoComplete="off"
                      ref={register({
                        required: "Company name is required",
                      })}
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row style={{ padding: 0 }}>
                <Grid.Column
                  width={4}
                  textAlign="right"
                  verticalAlign="middle"
                ></Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <ValidationError
                    errors={errors}
                    name="companyName"
                    pointing="above"
                    internal={true}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Domain name</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required error={!!errors.domain}>
                    <input
                      autoFocus
                      name="domain"
                      key="domain"
                      className={styles.myAccountInput}
                      placeholder="Enter domain name"
                      autoComplete="off"
                      ref={register({
                        required: "Domain name is required",
                        pattern: {
                          value: urlValidator,
                          message: "Please enter a valid domain name.",
                        },
                      })}
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row style={{ padding: 0 }}>
                <Grid.Column
                  width={4}
                  textAlign="right"
                  verticalAlign="middle"
                ></Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <ValidationError
                    errors={errors}
                    name="domain"
                    pointing="above"
                    internal={true}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Plan</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required>
                    <Dropdown
                      button
                      className={styles.myAccountInput}
                      name="plan"
                      key="plan"
                      selection
                      defaultValue={"free"}
                      options={planOptions}
                      onChange={handleDropdownChange}
                      icon={<Ico name="down" className={styles.dropdownIcon} />}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Address (Line 1)</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required>
                    <input
                      className={styles.myAccountInput}
                      placeholder="Street address"
                      name="addressLine1"
                      key="addressLine1"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Address (Line 2)</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required>
                    <input
                      className={styles.myAccountInput}
                      placeholder="Street address"
                      name="addressLine2"
                      key="addressLine2"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Town / City</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required>
                    <input
                      className={styles.myAccountInput}
                      placeholder="Town / City"
                      name="addressCity"
                      key="addressCity"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Zip Code</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required>
                    <input
                      className={styles.myAccountInput}
                      placeholder="Zip code / Postcode"
                      name="addressZip"
                      key="addressZip"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>State</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required>
                    <input
                      className={styles.myAccountInput}
                      placeholder="State / County"
                      name="addressState"
                      key="addressState"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Country</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field required error={!!errors.addressCountry}>
                    <Dropdown
                      className={styles.myAccountInput}
                      name="addressCountry"
                      key="addressCountry"
                      fluid
                      search
                      selection
                      options={countryOptions}
                      placeholder="Select Country"
                      value={inputs.addressCountry}
                      onChange={handleDropdownChange}
                      icon={<Ico name="down" className={styles.dropdownIcon} />}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Telephone 1</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field>
                    <input
                      className={styles.myAccountInput}
                      placeholder="Telephone 1"
                      name="addressTelephone1"
                      key="addressTelephone1"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                  <label>Telephone 2</label>
                </Grid.Column>
                <Grid.Column width={12} textAlign="left" stretched>
                  <Form.Field>
                    <input
                      className={styles.myAccountInput}
                      placeholder="Telephone 2"
                      name="addressTelephone2"
                      key="addressTelephone2"
                      onChange={handleInputChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>

          <div
            style={{
              padding: "2rem",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              secondary
              className={cStyles.buttonSecond}
              onClick={() => onClose()}
            >
              <Ico name="cancel" style={{ marginRight: "10px" }} />
              Cancel
            </Button>

            <Button primary className={cStyles.buttonPrime}>
              <Ico name="check" style={{ marginRight: "10px" }} />
              Create
            </Button>
          </div>
        </Form>

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => {
              setMessage(null);
              if (createSuccess) {
                onClose();
              }
            }}
          />
        )}
      </Modal>
    </Transition>
  );
};

export default CreateOrgDialog;
